import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { Sensor } from "@station/domain/models/sensor.model";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const VariableAlertSchema = z
    .object({
        id: z.number(),
        hoursElapsedToTrigger: z.number(),
        lowTriggerValue: z.number().optional(),
        highTriggerValue: z.number().optional(),
        sensor: z.custom<Sensor>((val) => val instanceof Sensor, {
            error: "Invalid Sensor instance",
        }),
        activeInStationsLabel: z.string().trim().optional(),
        activeInStationsIds: z.array(z.number()),
    })
    .readonly();
export type VariableAlert = z.output<typeof VariableAlertSchema>;

const SensorAlertSchema = z
    .object({
        id: zIdSchema,
        name: z.string().trim(),
        uniqueTrigger: z.boolean(),
        notifyAll: z.boolean(),
        isEnabled: z.boolean(),
        stations: z.record(z.string(), z.string().trim()).nullable(),
        lastNotificationAt: z.string().trim().nullable(),
        stationIds: z.array(z.number()),
        variableAlerts: z.array(VariableAlertSchema),
    })
    .brand<"SensorAlert">();

export type SensorAlertInputProps = z.input<typeof SensorAlertSchema>;
export type SensorAlertProps = z.output<typeof SensorAlertSchema>;

export class SensorAlert {
    private constructor(private readonly props: Readonly<SensorAlertProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get name(): string {
        return this.props.name;
    }

    get uniqueTrigger(): boolean {
        return this.props.uniqueTrigger;
    }

    get notifyAll(): boolean {
        return this.props.notifyAll;
    }

    get isEnabled(): boolean {
        return this.props.isEnabled;
    }

    get stations(): Nullable<Record<string, string>> {
        return this.props.stations;
    }

    get lastNotificationAt(): Nullable<string> {
        return this.props.lastNotificationAt;
    }

    get stationIds(): number[] {
        return this.props.stationIds;
    }

    get variableAlerts(): readonly Readonly<VariableAlert>[] {
        return this.props.variableAlerts;
    }

    static validate(
        props: Readonly<MaybeValidProps<SensorAlertInputProps>>,
    ): Result<SensorAlertProps, z.ZodError<SensorAlertProps>> {
        const result = SensorAlertSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<SensorAlertInputProps>>,
    ): Result<SensorAlertProps, PartialValid<typeof SensorAlert>> {
        const result = partialSafeParse(SensorAlertSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<SensorAlertInputProps>,
    ): Result<SensorAlert, z.ZodError<SensorAlertProps>> {
        return SensorAlert.validate(props).map(
            (validatedProps) => new SensorAlert(validatedProps),
        );
    }

    static createWithRecovery(
        props: SensorAlertInputProps,
    ): Result<SensorAlert, ModelError<typeof SensorAlert>> {
        return SensorAlert.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const recoveryProps: Unbrand<SensorAlertProps> = {
                    id: error.validProps.id,
                    name: "",
                    uniqueTrigger: false,
                    notifyAll: false,
                    isEnabled: false,
                    stations: null,
                    lastNotificationAt: null,
                    stationIds: [],
                    variableAlerts: [],
                };

                merge(recoveryProps, error.validProps);

                return new RecuperableModelError<typeof SensorAlert>(
                    new SensorAlert(recoveryProps as SensorAlertProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new SensorAlert(validatedProps));
    }

    toCreateProps(): SensorAlertInputProps {
        return {
            id: this.props.id.toCreateProps(),
            name: this.props.name,
            uniqueTrigger: this.props.uniqueTrigger,
            notifyAll: this.props.notifyAll,
            isEnabled: this.props.isEnabled,
            stations: this.props.stations,
            lastNotificationAt: this.props.lastNotificationAt,
            stationIds: this.props.stationIds,
            variableAlerts: this.props.variableAlerts,
        };
    }
}

const SensorAlertsSchema = z
    .object({
        sensorAlerts: z.array(
            z.custom<SensorAlert>((val) => val instanceof SensorAlert, {
                error: "Invalid SensorAlert instance",
            }),
        ),
        count: z.number().min(0),
    })
    .brand<"SensorAlerts">();

export type SensorAlertsInputProps = z.input<typeof SensorAlertsSchema>;
export type SensorAlertsProps = z.output<typeof SensorAlertsSchema>;

export class SensorAlerts {
    private constructor(private readonly props: Readonly<SensorAlertsProps>) {}

    get sensorAlerts(): readonly Readonly<SensorAlert>[] {
        return this.props.sensorAlerts;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): SensorAlerts {
        return SensorAlerts.create({
            sensorAlerts: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<SensorAlertsInputProps>>,
    ): Result<SensorAlertsProps, z.ZodError<SensorAlertsProps>> {
        const result = SensorAlertsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<SensorAlertsInputProps>,
    ): Result<SensorAlerts, z.ZodError<SensorAlertsProps>> {
        return SensorAlerts.validate(props).map(
            (validatedProps) => new SensorAlerts(validatedProps),
        );
    }
}

export interface SensorAlertUpdate {
    id: Id;
    name: string;
    uniqueTrigger: boolean;
    notifyAll: boolean;
    isEnabled: boolean;
    stationIds: number[];
    variableAlertsAttributes: {
        id: number;
        variableId: number;
        hoursElapsedToTrigger: number;
        lowTriggerValue: number | undefined;
        highTriggerValue: number | undefined;
    }[];
}
